// Renders the rich-content block model from src/content/richText.js.
// Headings carry stable `id` anchors so answers can be deep-linked — which both
// helps on-page navigation and lets AI engines cite a specific answer block.

/** @typedef {string | {text: string, bold?: boolean, italic?: boolean, href?: string}} Span */
/** @typedef {{type: string, id?: string, text?: string, spans?: Span[], items?: Array<Span | Span[]>, value?: React.ReactNode, label?: React.ReactNode, source?: React.ReactNode, headers?: React.ReactNode[], rows?: Array<Array<Span | Span[]>>}} RichBlock */

const linkStyle = { color: "#6b7280", fontWeight: 600 };

/** @param {{span: Span}} props */
const Span = ({ span }) => {
  if (typeof span === "string") return span;
  /** @type {React.ReactNode} */ let node = span.text;
  if (span.bold) node = <strong>{node}</strong>;
  if (span.italic) node = <em>{node}</em>;
  if (span.href) {
    const external = /^https?:\/\//.test(span.href);
    node = (
      <a
        href={span.href}
        style={linkStyle}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {node}
      </a>
    );
  }
  return node;
};

/** @param {{spans?: Span[]}} props */
const Spans = ({ spans = [] }) =>
  spans.map((span, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: static, order-stable content
    <Span key={i} span={span} />
  ));

/** @param {{items?: Array<Span | Span[]>}} props */
const ListItems = ({ items = [] }) =>
  items.map((item, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: static, order-stable content
    <li key={i}>
      <Spans spans={Array.isArray(item) ? item : [item]} />
    </li>
  ));

/** @type {React.CSSProperties} */
const calloutStyle = {
  background: "#f3f4f6",
  borderLeft: "4px solid #6b7280",
  borderRadius: "8px",
  padding: "16px 20px",
  margin: "20px 0",
};

/** @type {React.CSSProperties} */
const statStyle = {
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "24px",
  margin: "24px 0",
  textAlign: "center",
};

/** @type {React.CSSProperties} */
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  margin: "24px 0",
  fontSize: "15px",
};

/** @type {React.CSSProperties} */
const thStyle = {
  textAlign: "left",
  padding: "12px 16px",
  borderBottom: "2px solid #e5e7eb",
  background: "#f9fafb",
  fontWeight: 700,
};

/** @type {React.CSSProperties} */
const tdStyle = { padding: "12px 16px", borderBottom: "1px solid #f0f0f0", verticalAlign: "top" };

// Accept either rich `spans` or a plain `text` string (generated content uses text).
/** @param {RichBlock} block @returns {Span[]} */
const blockSpans = (block) => block.spans || (block.text != null ? [block.text] : []);

/** @param {{block: RichBlock}} props */
const Block = ({ block }) => {
  switch (block.type) {
    case "p":
      return (
        <p>
          <Spans spans={blockSpans(block)} />
        </p>
      );
    case "h2":
      return <h2 id={block.id}>{block.text}</h2>;
    case "h3":
      return <h3 id={block.id}>{block.text}</h3>;
    case "ul":
      return (
        <ul>
          <ListItems items={block.items} />
        </ul>
      );
    case "ol":
      return (
        <ol>
          <ListItems items={block.items} />
        </ol>
      );
    case "tldr":
      return (
        <div style={calloutStyle}>
          <strong>TL;DR. </strong>
          <Spans spans={blockSpans(block)} />
        </div>
      );
    case "callout":
      return (
        <div style={calloutStyle}>
          <Spans spans={blockSpans(block)} />
        </div>
      );
    case "stat":
      return (
        <div style={statStyle}>
          <div style={{ fontSize: "40px", fontWeight: 800, color: "#1a1a1a", lineHeight: 1.1 }}>
            {block.value}
          </div>
          {block.label && (
            <div style={{ fontSize: "16px", color: "#4a4a4a", marginTop: "8px" }}>
              {block.label}
            </div>
          )}
          {block.source && (
            <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "8px" }}>
              Source: {block.source}
            </div>
          )}
        </div>
      );
    case "table":
      return (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            {block.headers && (
              <thead>
                <tr>
                  {block.headers.map((h, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static columns
                    <th key={i} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {(block.rows || []).map((row, ri) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static rows
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static cells
                    <td key={ci} style={tdStyle}>
                      <Spans spans={Array.isArray(cell) ? cell : [cell]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
};

/** Render an array of blocks.
 * @param {{blocks?: RichBlock[]}} props */
const RichText = ({ blocks = [] }) =>
  blocks.map((block, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: static, order-stable content
    <Block key={i} block={block} />
  ));

export default RichText;
